import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { PreKPracticeSettings } from '../types';
import { DotPattern } from './types';
import { usePreKTTS, QUESTION_SCREEN_AUDIO } from '../../../hooks/usePreKAudio';
import { useAuth } from '../../../context/AuthContext';
import { useSession } from '../../../context/SessionContext';
import { setUserProgress } from '../../../config/api';
import { SetUserProgressRequest } from '../../../types/progress';
import { progressQueue } from '../../../services/ProgressQueueService';
import { NUMERAL_FONT_STYLE } from '../../PreKAssessment/preKConstants';
import {
  PracticeHeader,
  PRACTICE_PHASES,
  PRACTICE_TIMER_CONSTANTS,
  PRACTICE_CRITERIA,
  generatePracticeAnswerChoices,
  PREK_PRACTICE_TRACKS,
  PRACTICE_VISUAL_CONFIG,
} from '../shared';

interface AttemptRecord {
  factId: string;
  correct: boolean;
  timestamp: number;
}

interface AccuracyScreenProps {
  settings: PreKPracticeSettings;
  factsToPractice: { factId: string; digit: number }[];
  onComplete: () => void;
  onBack: () => void;
}

// Component to show dots for recent question presentations (within 2-minute engagement window)
const AttemptDotsIndicator: React.FC<{ attempts: AttemptRecord[] }> = ({ attempts }) => {
  // Filter to attempts within the last 2 minutes, then take last 10
  const now = Date.now();
  const windowStart = now - PRACTICE_CRITERIA.ENGAGEMENT_WINDOW_MS;
  const recentAttempts = attempts.filter(a => a.timestamp >= windowStart);
  const last10 = recentAttempts.slice(-PRACTICE_CRITERIA.ATTEMPT_WINDOW);

  const dots = [];
  for (let i = 0; i < PRACTICE_CRITERIA.ATTEMPT_WINDOW; i++) {
    const attempt = last10[i];
    let bgColor = 'bg-gray-300'; // Not yet attempted
    if (attempt) {
      bgColor = attempt.correct ? 'bg-green-500' : 'bg-red-500';
    }
    dots.push(
      <div
        key={i}
        className={`w-4 h-4 rounded-full ${bgColor} transition-colors duration-300`}
      />
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      {dots}
    </div>
  );
};

// Component to show transition message (inline, not overlay)
const TransitionMessage: React.FC<{ type: 'pass' | 'fail'; message: string }> = ({ type, message }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="text-center"
    >
      <div className="text-8xl mb-8">
        {type === 'pass' ? '🎉' : '💪'}
      </div>
      <h1 className="text-4xl lg:text-5xl font-bold">
        <span className={`bg-gradient-to-r ${
          type === 'pass'
            ? 'from-green-500 to-emerald-600'
            : 'from-blue-600 to-purple-600'
        } text-transparent bg-clip-text`}>
          {message}
        </span>
      </h1>
    </motion.div>
  );
};

const AccuracyScreen: React.FC<AccuracyScreenProps> = ({
  settings,
  factsToPractice,
  onComplete,
  onBack,
}) => {
  const { user } = useAuth();
  const { recordPageTransition } = useSession();
  const [currentFact, setCurrentFact] = useState<{ factId: string; digit: number } | null>(null);

  const [answerChoices, setAnswerChoices] = useState<number[]>([]);

  // Record page transition for Caliper tracking
  useEffect(() => {
    recordPageTransition('accuracy-practice');
  }, [recordPageTransition]);

  const [hasAnswered, setHasAnswered] = useState(false);
  const [ttsHasPlayed, setTtsHasPlayed] = useState(false);
  const [correctAnswerGiven, setCorrectAnswerGiven] = useState(false);

  // Subitizing-specific states
  const [flashPhase, setFlashPhase] = useState<'ready' | 'flashing' | 'blank' | 'question'>('ready');
  const [showDots, setShowDots] = useState(false);
  const [currentPattern, setCurrentPattern] = useState<DotPattern | null>(null);

  // Attempt history tracking for pass/fail criteria
  const [attemptHistory, setAttemptHistory] = useState<AttemptRecord[]>([]);
  const attemptHistoryRef = useRef<AttemptRecord[]>([]);

  // Track if current question presentation has been answered (to ignore retries)
  const [currentQuestionAnswered, setCurrentQuestionAnswered] = useState(false);

  // Transition message state
  const [transitionMessage, setTransitionMessage] = useState<{ type: 'pass' | 'fail'; message: string } | null>(null);

  // Timer tracking for attempt data
  const [questionStartTime, setQuestionStartTime] = useState<number>(Date.now());

  // Session start time for engagement tracking
  const [sessionStartTime] = useState<number>(Date.now());

  // Ref to store timeout IDs for cleanup
  const timeoutsRef = useRef<NodeJS.Timeout[]>([]);
  const isFirstQuestionRef = useRef(true);

  const { playTTSWithCallback, playChime, isTTSPlaying, playAudio } = usePreKTTS();

  const currentDigit = currentFact?.digit;

  // Select next fact, avoiding the same digit as current
  const selectNextFact = useCallback(() => {
    if (factsToPractice.length <= 1) {
      return factsToPractice[0];
    }
    let nextFact;
    do {
      const randomIndex = Math.floor(Math.random() * factsToPractice.length);
      nextFact = factsToPractice[randomIndex];
    } while (nextFact.digit === currentDigit);
    return nextFact;
  }, [factsToPractice, currentDigit]);

  // Generate dot pattern using 3x3 grid
  const generateDotPattern = useCallback((quantity: number): DotPattern => {
    const gridPositions = PRACTICE_VISUAL_CONFIG.SUBITIZING_GRID;

    // Shuffle positions and select the first 'quantity' positions
    const shuffledPositions = [...gridPositions].sort(() => Math.random() - 0.5);
    const selectedPositions = shuffledPositions.slice(0, quantity);

    // Create dots from selected positions
    const dots = selectedPositions.map((pos, i) => ({
      x: pos.x,
      y: pos.y,
      id: `dot-${i}`
    }));

    return {
      id: `pattern-${Date.now()}-${Math.random()}`,
      quantity,
      dots
    };
  }, []);

  // Keep attemptHistoryRef in sync
  useEffect(() => {
    attemptHistoryRef.current = attemptHistory;
  }, [attemptHistory]);

  // Check pass/fail criteria (each question presentation counts, retries don't)
  const checkCriteria = useCallback(() => {
    const history = attemptHistoryRef.current;
    const now = Date.now();
    const windowStart = now - PRACTICE_CRITERIA.ENGAGEMENT_WINDOW_MS;

    // Get last 10 question presentations
    const last10 = history.slice(-PRACTICE_CRITERIA.ATTEMPT_WINDOW);

    // Get attempts in last 2 minutes
    const recentAttempts = history.filter(a => a.timestamp >= windowStart);

    // Need at least 10 question presentations to evaluate pass/fail
    if (last10.length < PRACTICE_CRITERIA.ATTEMPT_WINDOW) {
      return { shouldPass: false, shouldFail: false };
    }

    // Calculate accuracy over last 10 questions
    const correctCount = last10.filter(a => a.correct).length;
    const accuracy = correctCount / last10.length;

    // Check engagement (8+ questions answered in last 2 min)
    const hasEngagement = recentAttempts.length >= PRACTICE_CRITERIA.MIN_ENGAGEMENT_ATTEMPTS;

    // Pass criteria: 90%+ accuracy AND 8+ answers in last 2 min
    const shouldPass = accuracy >= PRACTICE_CRITERIA.PASS_ACCURACY_THRESHOLD && hasEngagement;

    // Fail criteria: Below 70% over last 10 attempts
    const shouldFailLowAccuracy = accuracy < PRACTICE_CRITERIA.FAIL_ACCURACY_THRESHOLD;

    // Soft fail after 25 attempts without passing (PreK attention span safeguard)
    const hasIncorrectInLast10 = correctCount < PRACTICE_CRITERIA.ATTEMPT_WINDOW;
    const shouldFailTooManyAttempts =
      history.length >= PRACTICE_CRITERIA.MAX_ATTEMPTS_BEFORE_SOFT_FAIL && hasIncorrectInLast10;

    const shouldFail = shouldFailLowAccuracy || shouldFailTooManyAttempts;

    return { shouldPass, shouldFail, last10 };
  }, []);

  // Handle pass - all facts to fluency15Practice
  const handlePass = useCallback(async () => {
    if (!user?.userId) {
      onComplete();
      return;
    }

    // Show transition message and play audio (reusing number naming audio)
    setTransitionMessage({ type: 'pass', message: "Great job! Let's take the test!" });
    if (settings.speechEnabled) {
      playAudio(QUESTION_SCREEN_AUDIO.transitions.accuracyPass).catch(() => {});
    }

    try {
      const progressUpdate: SetUserProgressRequest = {
        facts: factsToPractice.reduce((acc, fact) => {
          acc[fact.factId] = { status: 'fluency15Practice' };
          return acc;
        }, {} as Record<string, { status: 'fluency15Practice' }>),
      };

      await setUserProgress(user.userId, progressUpdate);

      // Delay before redirect to show message (3s to allow audio to finish)
      setTimeout(() => {
        window.location.reload();
      }, 3000);
    } catch (error) {
      console.error('Failed to update facts to fluency15Practice:', error);
      onComplete();
    }
  }, [user?.userId, factsToPractice, onComplete, settings.speechEnabled, playAudio]);

  // Handle fail - struggling facts to learning
  const handleFail = useCallback(async (last10: AttemptRecord[]) => {
    if (!user?.userId) {
      onComplete();
      return;
    }

    // Show transition message and play audio (use assessment failure audio)
    setTransitionMessage({ type: 'fail', message: "Nice try! Let's practice it first!" });
    if (settings.speechEnabled) {
      playAudio({ filename: 'results/result_fail_subitizing.mp3' }).catch(() => {});
    }

    try {
      // Get facts that were wrong in last 10
      const strugglingFactIds = new Set<string>();
      last10.forEach(attempt => {
        if (!attempt.correct) {
          strugglingFactIds.add(attempt.factId);
        }
      });

      if (strugglingFactIds.size === 0) {
        // No struggling facts, just reload after delay (3s to allow audio to finish)
        setTimeout(() => {
          window.location.reload();
        }, 3000);
        return;
      }

      const progressUpdate: SetUserProgressRequest = {
        facts: Array.from(strugglingFactIds).reduce((acc, factId) => {
          acc[factId] = { status: 'learning' };
          return acc;
        }, {} as Record<string, { status: 'learning' }>),
      };

      await setUserProgress(user.userId, progressUpdate);

      // Delay before redirect to show message (3s to allow audio to finish)
      setTimeout(() => {
        window.location.reload();
      }, 3000);
    } catch (error) {
      console.error('Failed to update struggling facts:', error);
      onComplete();
    }
  }, [user?.userId, onComplete, settings.speechEnabled, playAudio]);

  // Initialize first fact when component loads
  useEffect(() => {
    if (!currentFact && factsToPractice.length > 0) {
      const randomIndex = Math.floor(Math.random() * factsToPractice.length);
      setCurrentFact(factsToPractice[randomIndex]);
    }
  }, [currentFact, factsToPractice]);

  // Initialize when current fact changes (new question presentation)
  useEffect(() => {
    if (currentDigit !== undefined) {
      setCurrentPattern(generateDotPattern(currentDigit));
      setAnswerChoices(generatePracticeAnswerChoices(PREK_PRACTICE_TRACKS.SUBITIZING, currentDigit));
      setHasAnswered(false);
      setTtsHasPlayed(false);
      setCorrectAnswerGiven(false);
      setCurrentQuestionAnswered(false);
      setShowDots(false);
      setFlashPhase('ready');
      setQuestionStartTime(Date.now());
    }
  }, [currentFact, currentDigit, generateDotPattern]);

  // Start the subitizing sequence when pattern changes
  useEffect(() => {
    if (currentPattern && flashPhase === 'ready' && !transitionMessage) {
      const startFlash = () => {
        setFlashPhase('flashing');
        setShowDots(true);

        // Flash for configured duration
        const timeoutId2 = setTimeout(() => {
          setShowDots(false);
          setFlashPhase('blank');

          // Brief blank period, then show question
          const timeoutId3 = setTimeout(() => {
            setFlashPhase('question');
          }, 100);
          timeoutsRef.current.push(timeoutId3);
        }, PRACTICE_TIMER_CONSTANTS.SUBITIZING_FLASH_MS);
        timeoutsRef.current.push(timeoutId2);
      };

      if (isFirstQuestionRef.current) {
        // First question: small delay before flash
        const timeoutId1 = setTimeout(startFlash, 500);
        timeoutsRef.current.push(timeoutId1);
      } else {
        // Subsequent questions: start flash immediately
        startFlash();
      }
    }
  }, [currentPattern, flashPhase, transitionMessage]);

  // Play TTS instruction (first question only)
  useEffect(() => {
    if (flashPhase === 'question' && !ttsHasPlayed) {
      if (isFirstQuestionRef.current && settings.speechEnabled && currentDigit !== undefined) {
        // First question: play TTS instruction
        const audioFile = {
          filename: 'subitizing_instruction.mp3',
          text: 'How many dots did you see?'
        };

        playTTSWithCallback(audioFile, () => {
          setTtsHasPlayed(true);
        }).catch(() => {
          setTtsHasPlayed(true);
        });
      } else {
        // Subsequent questions or speech disabled: skip TTS
        setTtsHasPlayed(true);
      }
    }
  }, [flashPhase, ttsHasPlayed, settings.speechEnabled, playTTSWithCallback, currentDigit]);

  // Cleanup all timeouts on unmount
  useEffect(() => {
    return () => {
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
    };
  }, []);

  const handleAnswerClick = useCallback(
    (selectedDigit: number) => {
      // Don't allow clicks while TTS is playing or already answered
      if (isTTSPlaying || hasAnswered || !currentFact) return;

      setHasAnswered(true);
      const isCorrect = selectedDigit === currentDigit;

      // Calculate response time
      const responseTime = Date.now() - questionStartTime;

      // Report attempt data to progress queue (all attempts for analytics)
      if (user?.userId && currentFact) {
        try {
          progressQueue.enqueueProgressUpdate(user.userId, {
            facts: {
              [currentFact.factId]: {
                attempts: 1,
                correct: isCorrect ? 1 : 0,
                timeSpent: responseTime,
              },
            },
          });
        } catch (error) {
          console.error('Error reporting Pre-K subitizing accuracy attempt:', error);
        }
      }

      // Only record FIRST attempt for this question presentation (retries don't count)
      if (!currentQuestionAnswered) {
        setCurrentQuestionAnswered(true);

        const newAttempt: AttemptRecord = {
          factId: currentFact.factId,
          correct: isCorrect,
          timestamp: Date.now(),
        };

        setAttemptHistory(prev => {
          const updated = [...prev, newAttempt];
          attemptHistoryRef.current = updated;
          return updated;
        });
      }

      if (isCorrect) {
        setCorrectAnswerGiven(true);

        // Play chime and check criteria
        playChime().then(() => {
          const timeoutId = setTimeout(() => {
            const { shouldPass, shouldFail, last10 } = checkCriteria();

            if (shouldPass) {
              handlePass();
            } else if (shouldFail && last10) {
              handleFail(last10);
            } else {
              // Continue practice - select new fact (avoiding same digit)
              isFirstQuestionRef.current = false;
              const nextFact = selectNextFact();
              setCurrentFact(nextFact);

              // Reset all subitizing states for new question
              setCurrentPattern(generateDotPattern(nextFact.digit));
              setAnswerChoices(generatePracticeAnswerChoices(PREK_PRACTICE_TRACKS.SUBITIZING, nextFact.digit));
              setHasAnswered(false);
              setCorrectAnswerGiven(false);
              setTtsHasPlayed(false);
              setCurrentQuestionAnswered(false);
              setShowDots(false);
              setFlashPhase('ready');
              setQuestionStartTime(Date.now());
            }
          }, 500);
          timeoutsRef.current.push(timeoutId);
        });
      } else {
        // Incorrect answer

        // Check criteria after recording attempt
        const timeoutId = setTimeout(() => {
          const { shouldPass, shouldFail, last10 } = checkCriteria();

          if (shouldPass) {
            handlePass();
          } else if (shouldFail && last10) {
            handleFail(last10);
          } else {
            // Continue practice - allow retry
            setHasAnswered(false);
          }
        }, PRACTICE_TIMER_CONSTANTS.WRONG_ANSWER_RETRY_MS);
        timeoutsRef.current.push(timeoutId);
      }
    },
    [
      currentDigit,
      hasAnswered,
      isTTSPlaying,
      playChime,
      currentFact,
      user,
      questionStartTime,
      checkCriteria,
      handlePass,
      handleFail,
      factsToPractice,
      currentQuestionAnswered,
      generateDotPattern,
    ]
  );

  if (!currentPattern || !currentFact) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex items-center justify-center">
        <div className="text-2xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex flex-col">
      <PracticeHeader
        phase={PRACTICE_PHASES.ACCURACY}
        onBack={onBack}
        customProgress={<AttemptDotsIndicator attempts={attemptHistory} />}
      />

      {/* Main Content Area */}
      <div className="flex-1 flex items-center justify-center px-6 py-6">
        {transitionMessage ? (
          <TransitionMessage type={transitionMessage.type} message={transitionMessage.message} />
        ) : (
          <div className="w-full max-w-md lg:max-w-lg">
            {/* Ready Phase: Waiting - only shown on first question */}
            {flashPhase === 'ready' && isFirstQuestionRef.current && (
              <div className="text-center">
                <div className="text-2xl lg:text-3xl text-gray-600 animate-pulse mb-2">Get ready...</div>
                <div className="text-lg text-gray-500">Watch carefully!</div>
              </div>
            )}

            {/* Dots Container - Uses the exact same area as the 4 option cards */}
            {flashPhase === 'flashing' && showDots && (
              <div className="grid grid-cols-2 gap-8 lg:gap-12 relative">
                {/* Create the exact same 4-card layout area */}
                <div className="h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl border-4 opacity-0"></div>
                <div className="h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl border-4 opacity-0"></div>
                <div className="h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl border-4 opacity-0"></div>
                <div className="h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl border-4 opacity-0"></div>

                {/* Dots positioned absolutely over this area - 3x3 grid positions */}
                {currentPattern.dots.map((dot) => (
                  <motion.div
                    key={dot.id}
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="absolute w-12 h-12 lg:w-16 lg:h-16 bg-blue-600 rounded-full shadow-lg"
                    style={{
                      left: `${dot.x}%`,
                      top: `${dot.y}%`,
                      transform: 'translate(-50%, -50%)'
                    }}
                  />
                ))}
              </div>
            )}

            {/* Blank Phase: Same exact area as cards */}
            {flashPhase === 'blank' && (
              <div className="grid grid-cols-2 gap-8 lg:gap-12">
                <div className="h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl border-4 opacity-0"></div>
                <div className="h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl border-4 opacity-0"></div>
                <div className="h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl border-4 opacity-0"></div>
                <div className="h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl border-4 opacity-0"></div>
              </div>
            )}

            {/* Answer Choices - 2x2 grid */}
            {flashPhase === 'question' && (
              <div className="grid grid-cols-2 gap-8 lg:gap-12">
                {answerChoices.map((choice, index) => (
                  <motion.button
                    key={choice}
                    initial={isFirstQuestionRef.current ? { opacity: 0, y: 30, rotate: -5 } : { opacity: 0 }}
                    animate={isFirstQuestionRef.current ? { opacity: 1, y: 0, rotate: 0 } : { opacity: 1 }}
                    transition={isFirstQuestionRef.current ? { duration: 0.4, delay: index * 0.1 } : { duration: 0.15 }}
                    whileHover={{ scale: 1.08, rotate: 1 }}
                    whileTap={{ scale: 0.92 }}
                    onClick={() => handleAnswerClick(choice)}
                    disabled={hasAnswered}
                    className={`
                      h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl transition-all duration-300
                      ${hasAnswered && choice === currentDigit
                        ? 'bg-green-500 text-white border-4 border-green-700 shadow-green-300'
                        : hasAnswered && choice !== currentDigit
                        ? 'bg-gray-300 text-gray-500 border-4 border-gray-400'
                        : 'bg-gradient-to-br from-blue-400 to-blue-600 hover:from-blue-500 hover:to-blue-700 text-white border-4 border-blue-700'
                      }
                      ${hasAnswered ? 'cursor-not-allowed' : 'cursor-pointer hover:shadow-2xl transform-gpu'}
                    `}
                    style={NUMERAL_FONT_STYLE}
                  >
                    {choice}
                  </motion.button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Audio Playing Indicator */}
      {isTTSPlaying && (
        <div className="flex justify-center pb-8">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center text-gray-600"
          >
            <div className="animate-pulse mr-2">🔊</div>
            <span>Listen carefully...</span>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AccuracyScreen;
